import { FootnoteReferenceV2 } from '@oneaudi/feature-app-utils';
import { Text } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import {
	DriveTrainKeyfactStyle,
	StyledKeyFactsContainer,
} from '~/app/compositions/sidebar/trim-line/components/drive-train-keyfacts-style';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { AvailableKeyFact, useMarketConfig } from '~/app/market-config';
import { KEY_FACT, ValueUnitLabel } from '~/app/market-config/models/technical-key-facts';
import { RenderHtml } from '~/components/text';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItemType } from '~/graphql/types';
import { state } from '~/storage';

// KeyFacts for the engine's card

export const useDriveTrainKeyFacts = () => {
	const { config, formatter, market } = useMarketConfig();
	const { translations } = useAppState();
	const { carIdentifier } = useConfigurator();
	const [carType] = useAtom(state.carType);

	const getKeyFacts = useCallback(
		(drive: CatalogItemType['driveTrain']) => {
			const configuredKeyFacts = KeyfactPHEVFilter(config.engineCardKeyfacts)?.map((fact) => KEY_FACT[fact]);

			if (!configuredKeyFacts || configuredKeyFacts.length === 0) {
				return [];
			}

			return configuredKeyFacts
				.map((keyFact) => {
					if (keyFact && keyFact.key in KEY_FACT && drive.techData) {
						const fact = keyFact.getFn(drive.techData, translations, formatter, market, carType);
						return fact;
					}

					return null;
				})
				.filter(Boolean) as ValueUnitLabel[];
		},
		[config.engineCardKeyfacts, translations, formatter, market, carIdentifier, carType],
	);
	return { getKeyFacts };
};

// Function to clean up PHEV consumption keyfacts because the naming was off and we cant remove already established keyfacts.
// does not matter if 2 or 3 are selected we will always show empty Battery and electric only, combustion is always the one removed/replaced
// when only combustion is selected we return the elctrical as it is the current behaviour and changing it could change current configs
export function KeyfactPHEVFilter(keyfacts: AvailableKeyFact[] | undefined) {
	if (keyfacts && keyfacts.length !== 0) {
		const EmptyBattery = keyfacts.find((keyfact) => keyfact === KEY_FACT.consumptionPHEVEmptyBattery.key);
		const Electric = keyfacts.find((keyfact) => keyfact === KEY_FACT.consumptionPHEVElectric.key);
		const indexCombustion = keyfacts.findIndex((keyfact) => keyfact === KEY_FACT.consumptionPHEVCombustion.key);

		if (EmptyBattery && Electric && indexCombustion !== -1) {
			keyfacts.splice(indexCombustion, 1);
		} else if (EmptyBattery && indexCombustion !== -1) {
			keyfacts[indexCombustion] = KEY_FACT.consumptionPHEVElectric.key;
		} else if (indexCombustion !== -1 && Electric) {
			keyfacts[indexCombustion] = KEY_FACT.consumptionPHEVEmptyBattery.key;
		}
	}

	return keyfacts;
}

export function DriveTrainKeyFacts({ drive }: { drive: CatalogItemType['driveTrain'] }) {
	const { getKeyFacts } = useDriveTrainKeyFacts();
	const keyFacts = useMemo(() => getKeyFacts(drive), [drive, getKeyFacts]);

	return keyFacts.length > 0 ? (
		<StyledKeyFactsContainer>
			{keyFacts.map(
				(keyFact, index) =>
					keyFact.label && <KeyFact index={index} key={`${keyFact.formattedValue}-${index}`} {...keyFact} />,
			)}
		</StyledKeyFactsContainer>
	) : null;
}

function KeyFact({ label, formattedValue, remarks, index }: ValueUnitLabel & { index: number }) {
	const { i18n } = useI18n();
	const {
		featureServices: { footnoteService },
	} = useAppServices();
	// TODO: We often receive duplicated remarks from CDB, so we just show the first one with a value
	const remark = remarks?.find((r) => r?.value && r.value.length > 0);

	// We need to check if the key is a FEFA key or not. When the key is not a FEFA key, we have to make sure to manually adjust the id of the footnote because there is a bug when trying to access a html element by id that only contains numbers.
	const footnoteId = remark ? (remark.id.includes('nemo-fn') ? remark.id : `keyfact-${remark.id}`) : '';

	if (remark) {
		const alreadyExistingFootnotes = footnoteService?.getData();
		// I would love to use a ternary here, but eslint blocks that and throws "Expected an assignment or function call and instead saw an expression" error when trying to commit, so I used if/else statements instead
		const footnoteFound = alreadyExistingFootnotes?.find((d) => d.Key === remark.id);
		if (footnoteFound) {
			footnoteService?.replaceFootNotes([{ Key: footnoteId, Text: remark.value ?? '' }]);
		} else {
			footnoteService?.addFootNotes([{ Key: footnoteId, Text: remark.value ?? '' }]);
		}
	}

	const id = `keyfact-${index}`;
	return (
		<DriveTrainKeyfactStyle tabIndex={0}>
			<Text aria-describedby={id} tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
				<RenderHtml html={label} />
				{remark && (
					<FootnoteReferenceV2
						footnoteId={footnoteId}
						footnoteAriaLabel={i18n('ngc.a11y.footnotes.technicalData', { technicalDataItem: label })}
					/>
				)}
			</Text>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: 'bold' }} id={id}>
				<RenderHtml html={formattedValue} />
			</Text>
		</DriveTrainKeyfactStyle>
	);
}
